import { AssetAnalysisItem } from './api';

// Placeholder analyses shown to Finnet guests behind the V3 regwall blur — plausible for any
// stock, replaced by real data once logged in. Separate file so the eventual mock cleanup is
// a one-file deletion, same convention as fundamental-scores.mock.ts.

// ~30 minutes ago, matching mockUpdatedAt() in fundamental-scores.mock.ts.
const GENERATED_AT = new Date(Date.now() - 30 * 60 * 1000).toISOString();

function daysAgo(days: number): string {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
}

// "DD.MM.YYYY" — the format parseHeadlineDate expects.
function headlineDate(days: number): string {
  const d = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

// ~5 months of weekly chart points — deterministic mild uptrend, not random.
const MOCK_CONSENSUS_HISTORY = Array.from({ length: 22 }, (_, i) => ({
  date: daysAgo((21 - i) * 7),
  price: Math.round((118 + i * 0.45 + Math.sin(i / 2.5) * 2.2) * 100) / 100,
  currency: 'EUR',
}));

// Raw backend shape for getAnalystRatings; published_at must stay within 12 months or the
// rating is filtered out.
const MOCK_RATINGS = [
  {
    analyst_name: 'Michael Weber',
    star_rating: 5,
    bank_name: 'Goldman Sachs',
    published_at: daysAgo(6),
    rating: 'Buy',
    price_target_low: 140,
    price_target_high: 150,
    success_rate: 0.68,
    average_return: 18.3,
    successful_ratings: 132,
    total_ratings: 194,
  },
  {
    analyst_name: 'Sarah Müller',
    star_rating: 4,
    bank_name: 'Deutsche Bank',
    published_at: daysAgo(19),
    rating: 'Buy',
    price_target_low: 138,
    price_target_high: 138,
    success_rate: 0.61,
    average_return: 12.4,
    successful_ratings: 98,
    total_ratings: 161,
  },
  {
    analyst_name: 'Thomas Kohl',
    star_rating: 3,
    bank_name: 'UBS',
    published_at: daysAgo(33),
    rating: 'Hold',
    price_target_low: 120,
    price_target_high: 128,
    success_rate: 0.55,
    average_return: 7.1,
    successful_ratings: 74,
    total_ratings: 135,
  },
  {
    analyst_name: 'Laura Fischer',
    star_rating: 4,
    bank_name: 'Morgan Stanley',
    published_at: daysAgo(41),
    rating: 'Buy',
    price_target_low: 145,
    price_target_high: 155,
    success_rate: 0.64,
    average_return: 15.2,
    successful_ratings: 105,
    total_ratings: 164,
  },
  {
    analyst_name: 'Jonas Berger',
    star_rating: 2,
    bank_name: 'Barclays',
    published_at: daysAgo(58),
    rating: 'Sell',
    price_target_low: 105,
    price_target_high: 112,
    success_rate: 0.47,
    average_return: -3.2,
    successful_ratings: 51,
    total_ratings: 108,
  },
];

// Values in millions (RevenueChart's contract).
const MOCK_EARNINGS_STATS = {
  eps_data: {
    quarterly: [
      { label: 'Q2 2025', currency: 'EUR', expected: 4.8, reported: 5.2 },
      { label: 'Q3 2025', currency: 'EUR', expected: 5.1, reported: 5.3 },
      { label: 'Q4 2025', currency: 'EUR', expected: 5.4, reported: 5.7 },
      { label: 'Q1 2026', currency: 'EUR', expected: 5.9, reported: 6.1 },
      { label: 'Q2 2026', currency: 'EUR', expected: 6.2, reported: null },
    ],
    yearly: [],
  },
  revenue_data: {
    yearly: [
      { label: 2023, currency: 'EUR', expected: 20800, reported: 21400 },
      { label: 2024, currency: 'EUR', expected: 23100, reported: 23800 },
      { label: 2025, currency: 'EUR', expected: 25900, reported: 26500 },
      { label: 2026, currency: 'EUR', expected: 29100, reported: null },
    ],
    quarterly: [
      { label: 'Q2 2025', currency: 'EUR', expected: 6300, reported: 6500 },
      { label: 'Q3 2025', currency: 'EUR', expected: 6600, reported: 6800 },
      { label: 'Q4 2025', currency: 'EUR', expected: 6900, reported: 7200 },
      { label: 'Q1 2026', currency: 'EUR', expected: 7100, reported: 7400 },
      { label: 'Q2 2026', currency: 'EUR', expected: 7600, reported: null },
    ],
  },
  profit_data: {
    yearly: [
      { label: 2023, currency: 'EUR', expected: 2450, reported: 2600 },
      { label: 2024, currency: 'EUR', expected: 2950, reported: 3100 },
      { label: 2025, currency: 'EUR', expected: 3400, reported: 3650 },
      { label: 2026, currency: 'EUR', expected: 4100, reported: null },
    ],
    quarterly: [
      { label: 'Q2 2025', currency: 'EUR', expected: 800, reported: 850 },
      { label: 'Q3 2025', currency: 'EUR', expected: 860, reported: 910 },
      { label: 'Q4 2025', currency: 'EUR', expected: 900, reported: 980 },
      { label: 'Q1 2026', currency: 'EUR', expected: 960, reported: 1050 },
      { label: 'Q2 2026', currency: 'EUR', expected: 1020, reported: null },
    ],
  },
};

// The viewed stock itself, highlighted as the table's first row — logo/name/isin get
// overridden from the real asset elsewhere (see V3CompetitorAnalysis.subject); only the
// numeric columns here are ever actually shown.
const MOCK_SUBJECT = {
  isin: 'DE0000000000',
  name: 'Musterwert AG',
  ticker: 'MUST',
  eps: { actual: 4.2, estimate: 3.9, sentiment: 'beat', surprise_percent: 7.7 },
  analyst: {
    consensus: 'buy',
    sentiment: 'positive',
    analyst_count: 24,
    average_price_target: 112.0,
    average_price_target_currency: 'EUR',
    price_target_upside_percent: 11.4,
  },
  traderfox: { quality: 11, growth: 10, dividend: 9, the_big_call: 11 },
  logo_url: null,
};

// Same peers as V2's MOCK_COMPETITION, plus a fourth to fill the overview's 4-row cap.
const MOCK_PEERS = [
  {
    isin: 'DE0007664039',
    name: 'Volkswagen AG',
    ticker: 'VOW3',
    eps: { actual: 22.4, estimate: 20.8, sentiment: 'beat', surprise_percent: 7.7 },
    analyst: {
      consensus: 'buy',
      sentiment: 'positive',
      analyst_count: 22,
      average_price_target: 138.0,
      average_price_target_currency: 'EUR',
      price_target_upside_percent: 14.6,
    },
    traderfox: { quality: 8, growth: 9, dividend: 12, the_big_call: 10 },
    logo_url: null,
    zero_link_isin: 'DE0007664039',
    zero_link_status: 'exact',
  },
  {
    isin: 'DE0005190003',
    name: 'BMW AG',
    ticker: 'BMW',
    eps: { actual: null, estimate: 16.5, sentiment: 'unknown', surprise_percent: null },
    analyst: {
      consensus: 'hold',
      sentiment: 'neutral',
      analyst_count: 18,
      average_price_target: 89.0,
      average_price_target_currency: 'EUR',
      price_target_upside_percent: 4.2,
    },
    traderfox: { quality: 9, growth: 7, dividend: 13, the_big_call: 9 },
    logo_url: null,
    zero_link_isin: 'DE0005190003',
    zero_link_status: 'exact',
  },
  {
    isin: 'DE000A1668T1',
    name: 'Mercedes-Benz Group',
    ticker: 'MBG',
    eps: { actual: 10.4, estimate: 11.2, sentiment: 'miss', surprise_percent: -7.1 },
    analyst: {
      consensus: 'buy',
      sentiment: 'positive',
      analyst_count: 21,
      average_price_target: 72.5,
      average_price_target_currency: 'EUR',
      price_target_upside_percent: 9.8,
    },
    traderfox: { quality: 7, growth: 6, dividend: 11, the_big_call: 8 },
    logo_url: null,
    zero_link_isin: 'DE000A1668T1',
    zero_link_status: 'exact',
  },
  {
    isin: 'DE000PAH0038',
    name: 'Porsche AG',
    ticker: 'P911',
    eps: { actual: 3.1, estimate: 2.9, sentiment: 'beat', surprise_percent: 6.9 },
    analyst: {
      consensus: 'buy',
      sentiment: 'positive',
      analyst_count: 24,
      average_price_target: 62.0,
      average_price_target_currency: 'EUR',
      price_target_upside_percent: 11.3,
    },
    traderfox: { quality: 12, growth: 11, dividend: 9, the_big_call: 13 },
    logo_url: null,
    zero_link_isin: null,
    zero_link_status: 'unknown',
  },
];

// One mock analysis per auth-gated type, substituted by findV3AnalysisSignal for guests. `news` is
// public for guests — its entry only backs V3NewsAnalysis's catalysts fallback and the
// analysis-missing case.
export const V3_GUEST_MOCK_ANALYSES: Record<string, AssetAnalysisItem> = {
  news: {
    analysis_type: 'news',
    output_type: 'llm',
    title: 'News',
    generated_at: GENERATED_AT,
    insights: {
      highlight: {
        title: 'Starke Quartalszahlen treiben die Aktie an',
        body: 'Das Unternehmen übertrifft die Erwartungen der Analysten bei Umsatz und Gewinn und hebt den Ausblick für das laufende Geschäftsjahr an.',
      },
      top_headlines: [
        {
          title: 'Quartalszahlen übertreffen die **Erwartungen** deutlich',
          date: headlineDate(2),
          body: 'Umsatz und Gewinn lagen klar über den Konsensschätzungen der Analysten.',
        },
        {
          title: 'Neue **Partnerschaft** stärkt die Marktposition',
          date: headlineDate(5),
          body: 'Eine strategische Kooperation soll den Zugang zu wichtigen Wachstumsmärkten beschleunigen.',
        },
        {
          title: 'Analysten heben ihre **Kursziele** an',
          date: headlineDate(9),
          body: 'Mehrere Banken reagieren auf die starken Zahlen mit höheren Kurszielen.',
        },
      ],
      short_term_catalysts: [
        {
          title: 'Erwarteter Gewinnanstieg im laufenden Quartal',
          body: 'Analysten prognostizieren eine deutliche Verbesserung der Margen und ein Umsatzwachstum im Vergleich zum Vorquartal.',
        },
        {
          title: 'Bevorstehende Produktneuheiten mit hohem Marktpotenzial',
          body: 'Die Einführung neuer Produkte könnte kurzfristig die Nachfrage ankurbeln und Marktanteile gegenüber Wettbewerbern ausbauen.',
        },
        {
          title: 'Regulatorische Weichenstellungen in wichtigen Absatzmärkten',
          body: 'Ausstehende Zulassungen und regulatorische Freigaben könnten den Zugang zu neuen Märkten erheblich beschleunigen.',
        },
      ],
      long_term_catalysts: [
        {
          title: 'Strategische Expansion in internationale Wachstumsmärkte',
          body: 'Gezielte Investitionen in aufstrebende Märkte bieten strukturelles Wachstumspotenzial und eine breitere Umsatzbasis.',
        },
        {
          title: 'Technologievorsprung als nachhaltiger Wettbewerbsvorteil',
          body: 'Kontinuierliche Forschungs- und Entwicklungsinvestitionen sichern die langfristige Marktführerschaft und Innovationsfähigkeit.',
        },
      ],
    },
  },
  analysts_analysis: {
    analysis_type: 'analysts_analysis',
    output_type: 'llm',
    title: 'Analysten',
    generated_at: GENERATED_AT,
    content: {
      summary_intro: {
        insights: [
          {
            title: 'Mehrheitlich positive Analystenstimmung',
            body: 'Die Mehrheit der Analysten bewertet das Unternehmen positiv und sieht weiteres Kurspotenzial in den kommenden Quartalen.',
          },
        ],
      },
      overview: {
        consensus_label: 'BUY',
        rating_counts: { buy: 18, hold: 8, sell: 2 },
        analyst_count: 28,
        price_target_summary: {
          consensus_target: 142.5,
          high_target: 168,
          low_target: 105,
          currency: 'EUR',
        },
      },
      ratings: MOCK_RATINGS,
      consensus_history: MOCK_CONSENSUS_HISTORY,
      positive_theses: [
        {
          title: 'Starkes Wachstum in Kernmärkten',
          body: 'Das Unternehmen profitiert von anhaltend hoher Nachfrage und Marktanteilsgewinnen in seinen wichtigsten Segmenten.',
        },
        {
          title: 'Solide Bilanzstruktur',
          body: 'Die robuste Finanzlage gibt dem Management Spielraum für strategische Investitionen und Kapitalrückflüsse an Aktionäre.',
        },
        {
          title: 'Attraktive Bewertung',
          body: 'Im historischen Vergleich erscheint die aktuelle Bewertung angesichts der Wachstumsaussichten moderat.',
        },
      ],
      negative_theses: [
        {
          title: 'Makroökonomische Risiken',
          body: 'Steigende Zinsen und eine mögliche Konjunkturabschwächung könnten die Bewertung unter Druck setzen.',
        },
        {
          title: 'Intensiver Wettbewerb',
          body: 'Aggressive Mitbewerber und Preisdruck in Schlüsselmärkten könnten die Margen mittelfristig belasten.',
        },
      ],
    },
  },
  earnings_report: {
    analysis_type: 'earnings_report',
    output_type: 'llm',
    title: 'Umsatz- und Gewinnerwartungen im abgelaufenen Quartal klar übertroffen',
    generated_at: GENERATED_AT,
    content: {
      report_period: { fiscal_year: 2026, fiscal_period: 'Q1' },
      summary: {
        body: 'Das Unternehmen erzielte ein starkes Quartalsergebnis mit deutlichen Zuwächsen in den Kernsegmenten. Das Management hob den Jahresausblick leicht an.',
      },
      outlook: {
        body: 'Für das kommende Quartal rechnet das Management mit weiterem moderatem Wachstum, gestützt durch anhaltend starke Nachfrage in den Wachstumssegmenten.',
      },
      updates: {
        body: {
          positive: [
            { title: 'Umsatzwachstum', body: 'Umsatz übertraf die Konsenserwartungen deutlich.' },
            {
              title: 'Margenausbau',
              body: 'Operative Marge verbesserte sich gegenüber dem Vorjahresquartal.',
            },
            {
              title: 'Ausblick angehoben',
              body: 'Management hob die Jahresprognose für Umsatz und Gewinn an.',
            },
          ],
          negative: [
            {
              title: 'Makrorisiken',
              body: 'Unsichere gesamtwirtschaftliche Lage bleibt ein Risikofaktor.',
            },
            {
              title: 'Wettbewerbsdruck',
              body: 'Zunehmender Wettbewerb in Kernmärkten könnte Margen belasten.',
            },
          ],
        },
      },
      earnings_stats: MOCK_EARNINGS_STATS,
    },
  },
  earnings_transcripts: {
    analysis_type: 'earnings_transcripts',
    output_type: 'llm',
    title: 'Earnings Call FAQ',
    generated_at: GENERATED_AT,
    content: {
      intro: {
        body: 'Der Earnings Call stand im Zeichen starker Quartalsergebnisse und eines positiven Ausblicks auf das laufende Geschäftsjahr.',
      },
      faq: {
        body: [
          {
            question: 'Wie bewertet das Management die aktuelle Marktentwicklung?',
            answer:
              'Das Management sieht die Nachfrage in den Kernsegmenten als stabil und erwartet weiteres moderates Wachstum.',
          },
          {
            question: 'Welche Maßnahmen plant das Unternehmen zur Margensteigerung?',
            answer:
              'Effizienzprogramme und gezielte Investitionen in wachstumsstarke Bereiche sollen die Profitabilität weiter verbessern.',
          },
          {
            question: 'Wie entwickelt sich das internationale Geschäft?',
            answer:
              'Die Expansion in internationale Wachstumsmärkte verläuft laut Management planmäßig und trägt zunehmend zum Umsatz bei.',
          },
        ],
      },
    },
  },
  competition: {
    analysis_type: 'competition',
    output_type: 'llm',
    title: 'Wettbewerb',
    generated_at: GENERATED_AT,
    content: {
      advantage:
        'Das Unternehmen positioniert sich durch überlegene Margen und starke Markenbindung als klarer Outperformer gegenüber seinen Wettbewerbern.',
      risk: 'Kostendruck durch steigende Rohstoffpreise und zunehmendes Wettbewerbsumfeld könnten die relative Stärke mittelfristig herausfordern.',
      subject: MOCK_SUBJECT,
      peers: MOCK_PEERS,
    },
  },
};
